// src/pages/DriverFuelCard.tsx
//
// Read-only driver-facing view of the fuel card for whichever vehicle
// they're rostered on today -- deliberately separate from the admin
// FuelCardsTab (Fleet Management), which shows/manages every card in the
// fleet. A driver only ever sees their own vehicle's card here, and can't
// add/edit/deactivate anything.
import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Fuel, Eye, EyeOff, RefreshCw } from "lucide-react";

interface FuelCardInfo {
  pool_number: string;
  card_type: string;
  pin: string | null;
  status: string;
}

interface VehicleFuelCardRow {
  vehicleNumber: string;
  card: FuelCardInfo | null;
}

const DriverFuelCard: React.FC<{ driverName: string }> = ({ driverName }) => {
  const [rows, setRows] = useState<VehicleFuelCardRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  const load = async () => {
    if (!driverName) return;
    setLoading(true);
    setError(null);
    try {
      const todayStr = format(new Date(), "yyyy-MM-dd");
      const { data: shifts, error: shiftsError } = await supabase
        .from("vw_roster_shifts_enriched")
        .select("vehicle_number")
        .eq("driver_name", driverName)
        .eq("shift_date", todayStr)
        .eq("status", "published")
        .not("vehicle_number", "is", null);
      if (shiftsError) throw shiftsError;

      const vehicleNumbers = Array.from(new Set((shifts || []).map((s) => s.vehicle_number as string)));

      if (vehicleNumbers.length === 0) {
        setRows([]);
        return;
      }

      const { data: assignments, error: assignError } = await supabase
        .from("vehicle_fuel_cards")
        .select("vehicle_number, fuel_cards ( pool_number, card_type, pin, status )")
        .in("vehicle_number", vehicleNumbers)
        .is("unassigned_at", null);
      if (assignError) throw assignError;

      const cardByVehicle = new Map<string, FuelCardInfo>();
      (assignments || []).forEach((a: any) => {
        if (a.fuel_cards) cardByVehicle.set(a.vehicle_number, a.fuel_cards as FuelCardInfo);
      });

      setRows(vehicleNumbers.map((v) => ({ vehicleNumber: v, card: cardByVehicle.get(v) || null })));
    } catch (e: any) {
      setError(e?.message ?? "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverName]);

  const toggleReveal = (vehicleNumber: string) => {
    setRevealed((prev) => ({ ...prev, [vehicleNumber]: !prev[vehicleNumber] }));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-xl font-semibold flex items-center gap-2">
          <Fuel className="h-5 w-5" />
          Fuel Card
        </CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-center py-8 text-muted-foreground">Loading...</p>
        ) : error ? (
          <p className="text-center py-8 text-destructive">{error}</p>
        ) : rows.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No vehicle rostered today.</p>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div key={row.vehicleNumber} className="rounded-md border p-4">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div>
                    <div className="text-xs text-muted-foreground">Vehicle Number</div>
                    <div className="font-medium">{row.vehicleNumber}</div>
                  </div>
                  {row.card ? (
                    <>
                      <div>
                        <div className="text-xs text-muted-foreground">Pool Number</div>
                        <div className="font-medium">{row.card.pool_number}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Fuel Type</div>
                        <div className="font-medium">{row.card.card_type}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">PIN</div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium">
                            {revealed[row.vehicleNumber] ? row.card.pin || "-" : row.card.pin ? "••••" : "-"}
                          </span>
                          {row.card.pin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => toggleReveal(row.vehicleNumber)}
                            >
                              {revealed[row.vehicleNumber] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">No fuel card assigned to vehicle {row.vehicleNumber}.</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DriverFuelCard;
